import React, { useMemo, useState } from 'react';
import { usePopper } from 'react-popper';
import styled, { css, keyframes } from 'styled-components';
import { promptName, useObserver } from '../../../CodesThatsGottaGoSomewhereElse';
import { Icon } from '../../../concepts/resources/Icon';
import { useResolve } from '../../Container';
import { PopperInner, PopperMount } from '../../canvas/ui/PopperElements';
import { ShowIfHasPermission } from '../../common/PermissionElements';
import { Colors, Float, Prompt } from './../../../ui';
import { ManageWaitingRoomView } from './ManageWaitingRoomView';
import { WaitingRoomEntry } from './WaitingRoomEntry';
import { WaitingRoomManager } from './WaitingRoomManager';

const down = keyframes`
    0% { background-color: ${Colors.positive}; }
    20% { background-color: ${Colors.primary}; }
    40% { background-color: ${Colors.mystery}; }
    60% { background-color: ${Colors.negative}; }
    80% { background-color: ${Colors.observe}; }
    100% { background-color: ${Colors.positive}; }
`;

const Frame = styled.div<{ active?: boolean }>`
    position: relative;
    padding: .5rem;
    background: ${Colors.positive};
    color: white;
    font-weight: 450;
    border-radius: .375rem;
    user-select: none;

    animation: ${down} 10s linear;
    animation-fill-mode: forwards;
    animation-iteration-count: infinite;

    & > img, svg, span {
        position: relative;
        z-index: 2;
        margin-right: .5rem;
        pointer-events: none;
    }

    &:after {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        content: '';
        border: solid .2rem rgba(0, 0, 0, 0.1);
        border-radius: .375rem;
    }

    &:hover:after { 
        cursor: pointer;
        border: solid .2rem rgba(0, 0, 0, 0.3);
    }

    &:active:after {
        background: rgba(0, 0, 0, 0.3);
    }

    ${p => !p.active ? '' : css`
        &:after {
            background: rgba(0, 0, 0, 0.3);
        }
    `}    
`;

const WaitingButton = React.memo<{ knockers: WaitingRoomEntry[] }>(({ knockers }) => {

    const [open, setOpen] = useState(false);
    const [refElmt, setRefElmt] = useState(null);
    const [popElmt, setPopElmt] = useState(null);

    const { styles, attributes } = usePopper(refElmt, popElmt, {
        placement: 'bottom-end',
    });

    const onToggle = () => setOpen(x => !x);
    const pn = promptName();

    return <>
        <Frame className={`ignore-${pn}`} ref={setRefElmt} active={open} onClick={onToggle}>
            <Icon size="1.5" src="deqo://icons/hero/bell" />
            <span>{knockers.length} {knockers.length < 2 ? 'person' : 'people'} waiting...</span>
        </Frame>
        {open && <PopperMount ref={setPopElmt} style={styles.popper} {...attributes.popper}>
            <Prompt name={pn} ignoreEnter ignoreEscape onAction={() => setOpen(false)}>
                <PopperInner direction="down">
                    <Float shadow="lg" inline>
                        <ManageWaitingRoomView knockers={knockers} />
                    </Float>
                </PopperInner>
            </Prompt>
        </PopperMount>}
    </>;
});

const WaitingWrapper = () => {
    const entries = useObserver(useResolve(WaitingRoomManager));
    const knockers = useMemo(() => Array.from(entries.values()).filter(x => x.state === 'waiting' || !x.state), [entries]);
    return !!knockers.length && <WaitingButton knockers={knockers} />;
};

export const WaitingRoomHeaderBlock = () => {
    return <ShowIfHasPermission requires={['da:write', 'wo:write', 'bo:write']}>
        <WaitingWrapper />
    </ShowIfHasPermission>;
};

